import {
  MailDelivery,
  MailedNoticeDocumentType,
  MAIL_PROVIDER,
  ShippingTier
} from '../types/mail';
import {
  AffidavitReconciliationSettings,
  MailingAddress
} from '../types/organization';
import { EOrganization } from '../types';
import { State } from '../enums';

/** Publisher-funded affidavit copies. The client never submits them — `reconcileNoticeTypeDefaultMail` writes them. */

export type AffidavitMailStamp = {
  documentType: MailedNoticeDocumentType;
  provider: MAIL_PROVIDER;
};

const toStateValue = (state: number | string): number | string =>
  typeof state === 'string' ? State.by_label(state)?.value ?? state : state;

export function getNoticeTypeDefaultAddresses(
  newspaper: Pick<EOrganization, 'allowedNotices'> | undefined,
  noticeType: number | undefined | null
): MailingAddress[] {
  if (!newspaper || noticeType === undefined || noticeType === null) {
    return [];
  }
  const match = newspaper.allowedNotices?.find(
    allowedNotice => allowedNotice.value === noticeType
  );
  return (match?.defaultMailingAddresses ?? []).map(mailingAddress => ({
    ...mailingAddress,
    address: {
      ...mailingAddress.address,
      address_state: toStateValue(mailingAddress.address.address_state)
    }
  }));
}

/** From settings rather than the UI selection, so the server can build without the client. */
export function affidavitMailStampFromSettings(
  settings: Partial<AffidavitReconciliationSettings> | null | undefined
): AffidavitMailStamp {
  if (
    settings?.notarizationVendor === 'manual' &&
    settings?.notarizationRequired === true
  ) {
    return {
      documentType: MailedNoticeDocumentType.WET_SIG_AFFIDAVIT,
      provider: 'manual'
    };
  }
  if (settings?.affidavitsManagedByColumn) {
    return {
      documentType: MailedNoticeDocumentType.RON_AFFIDAVIT,
      provider: 'lob'
    };
  }
  return {
    documentType: MailedNoticeDocumentType.STANDARD_AFFIDAVIT,
    provider: 'lob'
  };
}

/** Always Ground — the publisher covers these, so a customer's upgrade doesn't apply. */
export function deriveNoticeTypeDefaultMail(
  newspaper: Pick<EOrganization, 'allowedNotices'> | undefined,
  noticeType: number | undefined | null,
  stamp: AffidavitMailStamp
): MailDelivery[] {
  return getNoticeTypeDefaultAddresses(newspaper, noticeType).map(
    mailingAddress => ({
      description: mailingAddress.description ?? '',
      name: mailingAddress.name,
      address: mailingAddress.address,
      copies: mailingAddress.copies ?? 1,
      isCourthouse: mailingAddress.isCourthouse ?? false,
      ...(mailingAddress.courtHouse !== undefined && {
        courtHouse: mailingAddress.courtHouse
      }),
      documentType: stamp.documentType,
      provider: stamp.provider,
      shippingTier: ShippingTier.GROUND,
      isNoticeTypeDefault: true
    })
  );
}

/**
 * TODO: this marker waives every affidavit and shipping charge, but firestore.rules
 * lets any signed-in user write a notice's `mail` subcollection at any time. Two
 * known consequences: the exemption is forgeable, and pre-feature notices whose
 * preset rows lost the marker are delivered and billed twice. Restrict mail writes,
 * or move pricing-sensitive properties out of that collection.
 */
export const isPublisherFundedMail = (mail: MailDelivery): boolean =>
  mail.isNoticeTypeDefault === true;

export const getBillableMail = (
  mail: MailDelivery[] | undefined | null
): MailDelivery[] => (mail ?? []).filter(m => !isPublisherFundedMail(m));
